import { useCallback } from "react";
import { usePermissionsStore } from "@/stores/permissions";
import { useAuthStore } from "@/stores/auth";
import {
  checkPermissions,
  checkSubscriptionPermission,
  isSponsoredRole,
} from "@/lib/permissions";
import type { NavItem } from "./sidebarData";
import { useAiAssistantEnabled } from "@/features/settings/hooks/useSettings";
import {
  supplierCanAccessPath,
  workspaceHomePath,
} from "@/lib/workspaceAccess";

const AI_PATHS = new Set([
  "/chat",
  "/agents",
  "/bulk-upload",
  "/analytics/briefing",
]);

/**
 * Hook that exposes whether a nav item is visible given the current RBAC
 * permissions and subscription plan permissions. Sponsored users rely on
 * their resource owner's plan; their own RBAC permissions are still required.
 */
export function useNavPermissions() {
  const aiEnabled = useAiAssistantEnabled();
  const role = useAuthStore((s) => s.user?.role);
  const permissions = usePermissionsStore((s) => s.permissions);
  const subscriptionPermissions = usePermissionsStore(
    (s) => s.subscriptionPermissions,
  );
  const addonPermissions = usePermissionsStore((s) => s.addonPermissions);
  const loading = usePermissionsStore((s) => s.loading);
  const loaded = usePermissionsStore((s) => s.loaded);
  const permissionsLoaded = usePermissionsStore((s) => s.permissionsLoaded);
  const subscriptionLoaded = usePermissionsStore((s) => s.subscriptionLoaded);

  const canViewItem = useCallback(
    (item: NavItem): boolean => {
      if (!supplierCanAccessPath(role, item.path)) return false;
      if (AI_PATHS.has(item.path) && !aiEnabled) return false;
      const hasPermission = !item.permission
        ? true
        : permissionsLoaded && checkPermissions([item.permission], permissions);
      const hasPlan =
        isSponsoredRole(role) ||
        !item.planPermission ||
        (subscriptionLoaded &&
          checkSubscriptionPermission(
            item.planPermission,
            subscriptionPermissions,
            -1,
            addonPermissions,
          ));
      return hasPermission && hasPlan;
    },
    [
      aiEnabled,
      role,
      permissions,
      subscriptionPermissions,
      addonPermissions,
      permissionsLoaded,
      subscriptionLoaded,
    ],
  );

  return { canViewItem, loading, loaded, homePath: workspaceHomePath(role) };
}
